import { buffer, featureCollection, mask, polygon, union } from "@turf/turf";
import { Feature, MultiPolygon, Polygon } from "geojson";
import { isEqual } from "lodash";
import { GeoJSONFeature } from "maplibre-gl";
import { useEffect, useState } from "react";
import { useMap } from "react-map-gl/maplibre";

import { useMapListener } from "@nens/react/hooks";
import { useThrottledCallback } from "@nens/shared-utils";

import { VECTOR_LAYER_ID } from "@/config/map-layer-constants";

// MapLibre only exposes polygon data in tile-sized chunks. This function
// collects all those chunks so they can be merged back into the full polygon.
//
// Returns outer rings and holes separately so they can be unioned and then
// recombined while preserving the hole structure.
interface FragmentCollectionResult {
  outerRings: Feature<Polygon>[];
  allHoles: number[][][];
}

const collectFragments = (features: GeoJSONFeature[]): FragmentCollectionResult => {
  // outerRings: boundary fragments in [lng, lat] coords, arrives split across tiles
  // (e.g., Tile 1: [[0,0],[1,0],[1,1]], Tile 2: [[1,1],[2,1],[2,0]]).
  // allHoles: interior ring coords (e.g., [[[0.2,0.2],[0.8,0.2],[0.8,0.8]]] for one hole).
  const outerRings: Feature<Polygon>[] = [];
  const allHoles: number[][][] = [];

  for (const feature of features) {
    const { geometry } = feature;
    if (geometry.type === "Polygon") {
      const [outerRing, ...innerRings] = geometry.coordinates;
      // Collect outer ring
      outerRings.push({
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [outerRing] },
        properties: {},
      });
      // Collect all holes
      allHoles.push(...innerRings);
    } else if (geometry.type === "MultiPolygon") {
      for (const [outerRing, ...innerRings] of geometry.coordinates) {
        outerRings.push({
          type: "Feature",
          geometry: { type: "Polygon", coordinates: [outerRing] },
          properties: {},
        });
        allHoles.push(...innerRings);
      }
    }
  }

  return { outerRings, allHoles };
};

export function useInverseMask({
  isActive,
  sourceId,
}: {
  isActive: boolean;
  sourceId: string;
}): Feature<Polygon | MultiPolygon> | null {
  const { current: map } = useMap();
  const [maskGeoJson, setMaskGeoJson] = useState<Feature<Polygon | MultiPolygon> | null>(null);

  // Throttle this because sourceData is called ALOT when the map is moving, and we don't want to rebuild the mask that often.
  const buildMask = useThrottledCallback(() => {
    if (!map || !isActive) return;
    const source = map.getSource(sourceId);
    if (!source) return;

    // For vector tile sources, check if it's loaded
    if ("loaded" in source && !source.loaded()) return;

    const rawFeatures = map.querySourceFeatures(sourceId, { sourceLayer: VECTOR_LAYER_ID });
    if (!rawFeatures.length) return;

    // Collect outer rings and holes separately
    const { outerRings, allHoles } = collectFragments(rawFeatures);
    if (!outerRings.length) return;

    // Union all outer rings into a single geometry
    let unioned = outerRings.length > 1 ? union(featureCollection(outerRings)) : outerRings[0];

    if (!unioned || !unioned.geometry) return;

    // Clean geometry with buffer(0)
    const buffered = buffer(unioned, 0);
    if (!buffered || !buffered.geometry) return;
    unioned = buffered;

    // turf's `mask()` discards holes, so mask the outer shape first, then
    // union the hole shapes back in as their own solid polygon(s).
    let result: Feature<Polygon | MultiPolygon> = mask(unioned);

    if (allHoles.length > 0) {
      const holeFeatures = allHoles.map((hole) => polygon([hole]));
      const holesUnioned =
        holeFeatures.length > 1 ? union(featureCollection(holeFeatures)) : holeFeatures[0];

      if (holesUnioned) {
        const combined = union(featureCollection([result, holesUnioned]));
        if (combined) result = combined;
      }
    }

    setMaskGeoJson((prev) => {
      return isEqual(prev, result) ? prev : result;
    });
  }, 1000);

  // Rebuild mask whenever source data changes (new tiles loaded)
  useMapListener("sourcedata", buildMask, map);
  useEffect(buildMask, [buildMask, map, isActive, sourceId]);

  return isActive ? maskGeoJson : null;
}
